import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface SegmentedOption<T extends string | number> {
  value: T;
  label: string;
  /** Optional leading glyph (tiles variant) — e.g. a lucide icon element. */
  icon?: ReactNode;
}

export interface SegmentedControlProps<T extends string | number> {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (next: T) => void;
  'aria-label': string;
  /**
   * segment (default) — joined pill group inside one hairline border.
   * tiles — detached bordered tiles; with icons they stack icon-over-label.
   */
  variant?: 'segment' | 'tiles';
  /** Layout overrides for the group (e.g. stack on mobile). */
  className?: string;
}

/**
 * Single-choice control with full radiogroup semantics: role=radio,
 * aria-checked, roving tabindex, Arrow/Home/End navigation. This exact
 * pattern was hand-rolled four times across Settings before extraction.
 */
export function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  variant = 'segment',
  className,
}: SegmentedControlProps<T>) {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const count = options.length;
    const currentIndex = options.findIndex((o) => o.value === value);
    let nextIndex: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextIndex = (currentIndex + 1) % count;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
      nextIndex = (currentIndex - 1 + count) % count;
    else if (e.key === 'Home') nextIndex = 0;
    else if (e.key === 'End') nextIndex = count - 1;
    if (nextIndex === null) return;
    e.preventDefault();
    const nextOption = options[nextIndex];
    if (!nextOption) return;
    onChange(nextOption.value);
    buttonRefs.current[nextIndex]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={cn(
        variant === 'segment' ? 'inline-flex rounded-md border border-line-2 p-0.5' : 'flex gap-2',
        className,
      )}
    >
      {options.map((opt, index) => {
        const selected = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(opt.value)}
            className={cn(
              'transition-colors duration-press ease-out-strong',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
              variant === 'segment'
                ? cn(
                    'min-h-[36px] rounded px-3 text-body pointer-coarse:min-h-[44px]',
                    selected ? 'bg-brand-soft text-brand-ink' : 'text-t2 can-hover:hover:text-t1',
                  )
                : cn(
                    'min-h-[44px] rounded-md border px-3 py-2 text-body',
                    opt.icon
                      ? 'flex min-w-[64px] flex-col items-center justify-center gap-1 text-caption'
                      : 'text-start',
                    selected
                      ? 'border-brand-line bg-brand-soft text-brand-ink'
                      : 'border-line-2 text-t2 can-hover:hover:bg-rail',
                  ),
            )}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
